package xim.poc.game.configuration.v0.tactics

import xim.math.Vector3f
import xim.poc.ActorId
import xim.poc.ActorManager
import xim.poc.ModelLook
import xim.poc.NoOpActorController
import xim.poc.audio.SystemSound
import xim.poc.game.*
import xim.poc.game.actor.components.AugmentHelper
import xim.poc.game.actor.components.InventoryItem
import xim.poc.game.actor.components.getInventory
import xim.poc.game.configuration.*
import xim.poc.game.configuration.v0.*
import xim.poc.game.configuration.v0.interactions.NpcInteraction
import xim.poc.game.event.InitialActorState
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.InventoryUi
import xim.poc.ui.ShiftJis
import xim.resource.DatId
import xim.resource.Skill
import xim.util.PI_f
import kotlin.time.Duration.Companion.seconds

class TacticsBookEntity(val zone: TacticsZoneInstance, val location: Vector3f): FloorEntity {

    val promise = spawn()

    override fun update(elapsedFrames: Float) { }

    override fun cleanup() {
        promise.onReady { GameEngine.submitDeleteActor(it.id) }
    }

    private fun spawn(): ActorPromise {
        return GameEngine.submitCreateActorState(
            InitialActorState(
                name = "Ars Monstrum",
                type = ActorType.Effect,
                position = location,
                rotation = -PI_f/2f,
                modelLook = ModelLook.npc(0x981),
                movementController = NoOpActorController(),
                behaviorController = NoActionBehaviorId,
            )
        ).onReady {
            GameV0.interactionManager.registerInteraction(it.id, TacticsBookInteraction(zone))
        }
    }

}

class TacticsBookInteraction(val zone: TacticsZoneInstance): NpcInteraction {

    override fun onInteraction(npcId: ActorId) {
        val optionsProvider = {

            val options = if (TacticsState.getPlayerUnits().isNotEmpty()) {
                val challengeColor = if (TacticsState.hasClearedAnyItem()) { ShiftJis.colorClear } else { ShiftJis.colorGrey }

                mutableListOf(
                    QueryMenuOption("Item Battle", value = 1),
                    QueryMenuOption("Item Shop", value = 4),
                    QueryMenuOption("${challengeColor}Challenge Battle", value = 6),
                    QueryMenuOption("Manage Units", value = 2),
                    QueryMenuOption("Reorder Units", value = 5),
                )
            } else {
                mutableListOf()
            }

            if (TacticsState.getHirableJobs().isNotEmpty()) {
                options += QueryMenuOption("Hire Units", value = 3)
            }

            options += QueryMenuOption("Nothing", value = -1)
            options
        }

        UiStateHelper.openQueryMode({ "What will you do?" }, optionsProvider, visibleOptions = 6) { handleOpen(npcId, it) }
    }

    override fun maxInteractionDistance(npcId: ActorId): Float {
        return 3f
    }

    private fun handleOpen(npcId: ActorId, option: QueryMenuOption?): QueryMenuResponse {
        if (option == null || option.value < 0) { return QueryMenuResponse.pop }

        if (option.value == 1) {
            val items = getItems()
            if (items.isEmpty()) {
                ChatLog("No items to enhance! Purchase one from the ${ShiftJis.colorWhite}Item Shop${ShiftJis.colorClear} first.", ChatLogColor.Error)
                return QueryMenuResponse.noop(SystemSound.Invalid)
            }

            val itemOptions = items.mapIndexed(this::getItemOption) + QueryMenuOption("Cancel", -1)
            UiStateHelper.openQueryMode(
                prompt = "Which item will you enhance?",
                options = itemOptions,
                visibleOptions = 6,
                drawFn = { drawItem(it, items) },
            ) { handleMatchSelect(it, items) }
        } else if (option.value == 2) {
            openManagementQuery()
        } else if (option.value == 3) {
            TacticsHireUi(zone).push()
        } else if (option.value == 4) {
            TacticsItemShop.push(npcId)
        } else if (option.value == 5) {
            TacticsReorderUi(this).push()
        } else if (option.value == 6) {
            if (!TacticsState.hasClearedAnyItem()) {
                ChatLog("Upgrade at least one item in ${ShiftJis.colorWhite}Item Battle${ShiftJis.colorClear} first.", ChatLogColor.Error)
                return QueryMenuResponse.noop(SystemSound.Invalid)
            }

            TacticsChallengeUi(zone).push()
        }

        return QueryMenuResponse.noop
    }

    private fun handleMatchSelect(option: QueryMenuOption?, items: List<InventoryItem>): QueryMenuResponse {
        if (option == null || option.value < 0) { return QueryMenuResponse.pop }

        val item = items.getOrNull(option.value) ?: return QueryMenuResponse.popAll
        UiStateHelper.clear()

        val itemChallenge = TacticsChallengeConfigs[item]
        if (itemChallenge != null) {
            TacticsChallengeUi.enterChallenge(zone, itemChallenge)
            return QueryMenuResponse.noop
        }

        EventScriptRunner.runScript(EventScript(listOf(
            FadeOutEvent(0.25.seconds),
            WaitRoutine(0.20.seconds),
            TacticsPillarToggle(show = false),
            RunOnceEventItem {
                val match = TacticsMatchInstance(TacticsItemMatch(item))
                match.arenaUi.push()
                zone.setMatch(match)
            },
            FadeInEvent(0.25.seconds),
        )))

        return QueryMenuResponse.noop
    }

    private fun getItems(): List<InventoryItem> {
        val maximumItemLevel = if (TacticsState.hasClearedChallenge(tacticsChallenge_Remnants)) {
            34
        } else if (TacticsState.hasClearedChallenge(tacticsChallenge_Nought)) {
            29
        } else if (TacticsState.hasClearedChallenge(tacticsChallenge_BattaruRoyale)) {
            24
        } else if (TacticsState.hasClearedChallenge(tacticsChallenge_AncientVows)) {
            19
        } else if (TacticsState.hasClearedChallenge(tacticsChallenge_Frontier)) {
            14
        } else if (TacticsState.hasClearedChallenge(tacticsChallenge_Emissary)) {
            9
        } else {
            4
        }

        return ActorStateManager.player().getInventory().inventoryItems.filter(this::isValidItem)
            .filter { getItemLevel(it) <= maximumItemLevel }
            .sortedByDescending { getItemLevel(it) }
    }

    private fun getItemOption(index: Int, inventoryItem: InventoryItem): QueryMenuOption {
        val baseLevel = getItemLevel(inventoryItem)
        val color = AugmentHelper.getQualityColorDisplay(inventoryItem)
        val text = "$color${inventoryItem.info().name}${ShiftJis.colorClear} ${ShiftJis.leftBracket}M.Lv.${baseLevel}+${ShiftJis.rightBracket}"
        return QueryMenuOption(text, index)
    }

    private fun getItemLevel(inventoryItem: InventoryItem): Int {
        val challenge = TacticsChallengeConfigs[inventoryItem]
        return challenge?.displayLevel ?: TacticsItemMatch.getStartingLevel(inventoryItem)
    }

    private fun isValidItem(inventoryItem: InventoryItem): Boolean {
        if (inventoryItem.info().skill() != Skill.None) { return false }
        val itemDefinition = ItemDefinitions.getNullable(inventoryItem) ?: return false
        if (TacticsChallengeConfigs[inventoryItem] != null) { return true }
        if (!itemDefinition.ranked) { return false }
        return inventoryItem.augments != null
    }

    private fun openManagementQuery() {
        val units = TacticsState.getPlayerUnits()

        val options = { units.map { QueryMenuOption(it.name, it.id) } + QueryMenuOption("Cancel", -1) }

        UiStateHelper.openQueryMode(
            promptProvider = {"Manage which unit?" },
            optionsProvider = options,
            drawFn = { drawUnit(it) },
            visibleOptions = 6,
        ) {
            handleManagementChoice(it)
        }
    }

    private fun handleManagementChoice(option: QueryMenuOption?): QueryMenuResponse {
        val unit = TacticsState.getPlayerUnits().firstOrNull { it.id == option?.value }

        if (unit == null) {
            zone.portraitDummy.reset()
            return QueryMenuResponse.pop
        }

        val preview = TacticsUnitSkillPreview(unit)

        UiStateHelper.openQueryMode(
            promptProvider = { "What will ${unit.name} do?" },
            optionsProvider = { getManagementOptions(unit) },
            drawFn = { drawUnit(unit) },
            childStates = { listOf(preview.skillPreview) },
            keyHandler = { skillPreviewKeyHandler(preview.skillPreview) } ,
            visibleOptions = 6,
        ) {
            handleUnitManagementChoice(unit, it)
        }

        return QueryMenuResponse.noop
    }

    private fun getManagementOptions(unit: TacticsUnit): List<QueryMenuOption> {
        val options = ArrayList<QueryMenuOption>()
        options += QueryMenuOption("Change name", 2)

        if (TacticsState.hasUnlockedJobChange()) { options += QueryMenuOption("Change jobs", 1) }

        val rankUpText = getRankUpOption(unit)
        if (TacticsState.hasUnlockedRankUp() && rankUpText != null) { options += QueryMenuOption(rankUpText, 4) }

        if (TacticsState.hasUnlockedInheritance()) { options += QueryMenuOption("Inheritance", 5) }

        if (TacticsState.getPlayerUnits().size > 1) {
            options += QueryMenuOption("Delete unit", 3)
        }

        options += QueryMenuOption("Cancel", -1)

        return options
    }

    private fun getRankUpOption(unit: TacticsUnit): String? {
        val validation = unit.validateRankUp()

        return when(validation) {
            is RankUpSuccess -> "${ShiftJis.colorGold}Rank up${ShiftJis.colorClear}"
            is RankUpFailure -> {
                if (validation.requiredLevel == null) { return null }
                "${ShiftJis.colorGrey}Rank up (Req: Unit Lv.${validation.requiredLevel})"
            }
        }
    }

    private fun handleUnitManagementChoice(unit: TacticsUnit, option: QueryMenuOption?): QueryMenuResponse {
        if (option == null || option.value < 0) { return QueryMenuResponse.pop }

        if (option.value == 1) {
            TacticsJobChangeUi(unit, this, zone).push()
        } else if (option.value == 2) {
            val options = tacticsUnitNames.mapIndexed { i, it -> QueryMenuOption(it, i) }
            UiStateHelper.openQueryMode("Which name?", options, drawFn = { drawUnit(unit) }, visibleOptions = 6) { handleChangeName(unit, it) }
        } else if (option.value == 3) {
            UiStateHelper.pushState(UiStateHelper.yesNoMenu(
                text = "Really delete ${unit.name}?",
                onYes = { handleDeleteUnit(unit) },
                onNo = { UiStateHelper.popState(SystemSound.MenuClose) },
                cursorPos = 1,
            ))
        } else if (option.value == 4) {
            return handleSelectRankUp(unit)
        } else if (option.value == 5) {
            TacticsInheritanceUi(unit).push()
        }

        return QueryMenuResponse.noop
    }

    private fun handleSelectRankUp(unit: TacticsUnit): QueryMenuResponse {
        val validation = unit.validateRankUp()
        if (validation is RankUpFailure) {
            ChatLog(validation.reason, ChatLogColor.Error)
            return QueryMenuResponse.noop(SystemSound.Invalid)
        }

        UiStateHelper.openQueryMode(
            prompt = "Reset unit level to increase ${unit.name}'s rank?",
            options = listOf(
                QueryMenuOption("Yes!", 0),
                QueryMenuOption("No.", -1)
            ), drawFn = { drawUnit(unit) },
        ) {
            if (it == null || it.value < 0) {
                QueryMenuResponse.pop(SystemSound.MenuClose)
            } else {
                handleRankUp(unit)
                QueryMenuResponse.pop(SystemSound.MenuSelect)
            }
        }

        return QueryMenuResponse.noop
    }

    private fun handleRankUp(unit: TacticsUnit) {
        if (!unit.rankUp()) { return }
        val dummy = ActorManager[zone.portraitDummy.promise.getIfReady()] ?: return
        MiscEffects.playEffect(actor = dummy, target = dummy, fileTableIndex = 0xEEA, effectId = DatId.main)
    }

    private fun handleChangeName(unit: TacticsUnit, option: QueryMenuOption?): QueryMenuResponse {
        if (option == null || option.value < 0) { return QueryMenuResponse.pop }
        val newName = tacticsUnitNames.getOrNull(option.value) ?: return QueryMenuResponse.pop
        unit.name = newName
        return QueryMenuResponse.pop
    }

    private fun handleDeleteUnit(unit: TacticsUnit) {
        TacticsState.removeUnit(unit)
        UiStateHelper.popState(SystemSound.MenuClose)
        UiStateHelper.popState()
    }

    fun drawUnit(option: QueryMenuOption) {
        val unit = TacticsState.getPlayerUnits().firstOrNull { it.id == option.value } ?: return
        drawUnit(unit)
    }

    fun drawUnit(unit: TacticsUnit, jobId: TacticsJobId = unit.currentJob) {
        TacticsUnitDescription.drawUnitDescription(unit = unit, type = ActorType.AllyNpc, jobId = jobId)
        zone.portraitDummy.show(Vector3f(x=280f,y=8f,z=297f))
    }

    private fun drawItem(queryMenuOption: QueryMenuOption, items: List<InventoryItem>) {
        val item = items.getOrNull(queryMenuOption.value) ?: return
        val description = GameV0Helpers.getItemDescriptionInternal(inventoryItem = item)
        InventoryUi.drawSelectedInventoryItem(item, itemDescription = description)
    }

}